import { LOCATION_KEY, LOCATION_KEYS } from './locationFilter'

/**
 * Op welke locaties een deelnemer werkt.
 *
 * Sinds 2026-07-06 hangt de werklocatie aan de ROOSTERREGEL en niet meer aan de
 * deelnemer (`docs/migrations/schedule-row-locations.md`): iemand kan maandag in
 * Noord en woensdag in Zuid staan, en dat kon het oude veld niet uitdrukken. De
 * UI van dat oude veld is toen weggehaald.
 *
 * Alleen: de filters bleven het óude veld lezen, en het nieuwe werd nergens
 * gelezen. Het veld dat je kon zetten deed niets; het veld dat werkte kon je
 * niet zetten. Dit is de brug — inbox #264.
 *
 * AI-CONTEXT: See docs/features/huishouden-locaties.md
 */

/**
 * Het veld waarin een verrijkte deelnemer zijn locaties draagt.
 *
 * Meervoud, want dat is de kern van de verhuizing. Hier alleen doorgegeven en
 * niet opnieuw opgeschreven: `recordInLocation` leest exact deze sleutel, en
 * twee losse tekenreeksen met dezelfde waarde gaan vroeg of laat uiteenlopen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * @type {string}
 */
const PERSONA_LOCATIONS_KEY = LOCATION_KEYS

const gevuld = (v) => typeof v === 'string' && v.trim() !== ''

/**
 * Bouwt `{ deelnemer-ID: [locatie-docids] }` uit de roosterregels.
 *
 * @param {Array<object>} [scheduleTasks] - Documenten uit Tasks
 * @returns {Record<string, Array<string>>}
 */
const buildPersonaLocationIndex = (scheduleTasks) => {
    const index = {}

    ;(Array.isArray(scheduleTasks) ? scheduleTasks : []).forEach((task) => {
        if (!task || task.Deleted === true) return
        if (task.Type !== 'personaSchedule') return
        if (!gevuld(task.PersonaID) || !gevuld(task[LOCATION_KEY])) return

        const locatie = task[LOCATION_KEY].trim()
        if (!index[task.PersonaID]) index[task.PersonaID] = []
        if (!index[task.PersonaID].includes(locatie)) index[task.PersonaID].push(locatie)
    })

    return index
}

/**
 * Geeft elke deelnemer zijn locaties mee.
 *
 * **Met terugval op het oude veld**, en dat is geen nettigheid: gemeten op
 * 2026-09-22 hebben 35 van de 682 deelnemers wél het oude veld en géén
 * roosterregel met een locatie. Zonder terugval vallen die stil uit elk filter —
 * erger dan de fout die we repareren. Waar beide er zijn (55 deelnemers) spraken
 * ze elkaar nergens tegen, dus de voorrang van het rooster kost niets.
 *
 * @param {Array<object>} [personas]
 * @param {Record<string, Array<string>>} [index] - Uit `buildPersonaLocationIndex`
 * @returns {Array<object>} Dezelfde deelnemers, met `LocationIDs` erbij
 */
const withPersonaLocations = (personas, index) => {
    const kaart = index || {}

    return (Array.isArray(personas) ? personas : []).map((persona) => {
        const viaRooster = kaart[persona?.ID]
        const locaties = (Array.isArray(viaRooster) && viaRooster.length > 0)
            ? viaRooster
            : (gevuld(persona?.[LOCATION_KEY]) ? [persona[LOCATION_KEY]] : [])

        return { ...persona, [PERSONA_LOCATIONS_KEY]: locaties }
    })
}

export { PERSONA_LOCATIONS_KEY, buildPersonaLocationIndex, withPersonaLocations }
